import {
  AccountInfo,
  AuthenticationResult,
  EndSessionRequest,
  EventType,
  IPublicClientApplication,
  InteractionStatus,
  PublicClientApplication,
  RedirectRequest,
} from "@azure/msal-browser";
import { IdentityClaims, IdentityHelper } from "./Identity.Helper";

import { ConfigurationProvider } from "../configuration/ConfigurationProvider";
import { IdentityNavigationClient } from "./Identity.NavigationClient";
import { IdentityProvider } from "./IdentityProvider";
import { UrlUtility } from "../../utils/UrlUtility";

export class IdentityService {
  static redirectUri() {
    return ConfigurationProvider.getConfiguration().App.BaseUrl;
  }

  static Global: IdentityService;
  protected static navigationClient: IdentityNavigationClient;
  protected static isAuthenticated: boolean = false;
  protected static inProgress: InteractionStatus;


  /**
   * Bumped whenever the identity configuration changes shape. A stored value that does not match
   * means the cached MSAL state was written by a different configuration.
   */
  static readonly IDENTITY_CACHE_SCHEMA = "entra-external-id-v1";

  /**
   * One-time cleanup of Azure AD B2C cache residue, run before MSAL imports the cache.
   *
   * MSAL exposes no cache-key prefix, and changing the client id does not isolate everything -
   * `msal.account.keys` and the interaction state are shared across configurations. So a browser
   * whose last sign-in was under B2C still carries state that the Entra External ID configuration
   * cannot redeem, and MSAL tries to complete an interaction that no longer exists. The user gets
   * Microsoft's error page rather than a login:
   *   AADSTS900144: The request body must contain the following parameter: 'code'.
   *
   * Only fires when B2C residue is actually present, so a browser already on External ID is left
   * alone instead of being signed out for no reason. Wrapped in try/catch because storage can be
   * unavailable (private windows, blocked site data), and failing to migrate must never block boot.
   */
  protected static purgeLegacyB2CCache() {
    try {
        const schemaKey =
            ConfigurationProvider.getConfiguration().App.Name.replace("-", "_") + "_STORAGE_IDENTITY_SCHEMA";
        if (window.localStorage.getItem(schemaKey) === IdentityService.IDENTITY_CACHE_SCHEMA) return;

        const keys = Object.keys(window.localStorage);
        const isB2C = (k: string) =>
            k.indexOf("b2clogin.com") >= 0 || (window.localStorage.getItem(k) || "").indexOf("b2clogin.com") >= 0;

        if (keys.some(isB2C)) {
            for (const k of keys) {
                // msal.* is MSAL's own shared state (account index, interaction status, request params);
                // the claims blob is ours and holds B2C claims. Both are unusable after the migration.
                if (k.indexOf("msal.") === 0 || isB2C(k) || k.indexOf("_STORAGE_IDENTITY_CLAIMS") >= 0) {
                    window.localStorage.removeItem(k);
                }
            }
            window.sessionStorage.clear();
        }

        window.localStorage.setItem(schemaKey, IdentityService.IDENTITY_CACHE_SCHEMA);
    } catch {
        /* storage unavailable - nothing to migrate, and boot must not be blocked */
    }
  }

  static initInstance(msalInstance: PublicClientApplication) {
    // Before anything touches the cache: clear B2C residue that MSAL cannot redeem.
    IdentityService.purgeLegacyB2CCache();

    if (!msalInstance) {
      throw new Error("Invalid MSAL Client");
    }

    IdentityService.Global = new IdentityService(msalInstance);

    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      msalInstance.setActiveAccount(accounts[0]);
    }

    msalInstance.addEventCallback((event) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
        const payload = event.payload as AuthenticationResult;
        const account = payload.account;
        msalInstance.setActiveAccount(account);
      }
      if (event.error) {
        console.warn("MSAL Error", event);
      }


      if (event && event.eventType === EventType.POPUP_OPENED && event.payload) {
        const popup: Window = (event.payload as any)['popupWindow'];
        if(popup) {
          popup.onblur = function(event) {
            popup.focus();
          }
        }
      }

     // console.log(event);
    });
  }

  static userIsAuthenticated(): boolean {
    return IdentityService.isAuthenticated;
  }

  static setAuthenticated(isAuthenticated: boolean) {
    IdentityService.isAuthenticated = isAuthenticated;
    if (!isAuthenticated) {
      IdentityService.Global.saveClaims(null);
    }
  }

  static setInProgress(progress: InteractionStatus) {
    IdentityService.inProgress = progress;
  }

  static getInProgress() {
    return IdentityService.inProgress;
  }

  static setNavigationClient(client: IdentityNavigationClient) {
    IdentityService.navigationClient = client;
  }

  /** -------------------------------------------------------------
   *
   *  Identity Service With MSAL
   *
   *  ------------------------------------------------------------- */

  /** localStorage key holding the address and federated provider of the last sign-in. */
  protected static signInHintKey() {
    return ConfigurationProvider.getConfiguration().App.Name.replace("-", "_") + "_STORAGE_IDENTITY_SIGNIN_HINT";
  }

  /** Reads the record kept aside for the next sign-in. Never throws on a missing or corrupt value. */
  protected static readSignInHint(): { loginHint?: string; domainHint?: string } {
    try {
        return JSON.parse(window.localStorage.getItem(IdentityService.signInHintKey()) || "{}");
    } catch {
        return {};
    }
  }

  /** Keeps this session's address and provider aside; both have to outlive the Session Expired reset. */
  protected static saveSignInHint(claims: IdentityClaims) {
    const hint = {
        loginHint: IdentityHelper.getLoginHintFromClaims(claims),
        domainHint: IdentityHelper.getDomainHint(null, ConfigurationProvider.getConfiguration().Identity.FederatedIdps, claims),
    };
    if (hint.loginHint || hint.domainHint) {
        window.localStorage.setItem(IdentityService.signInHintKey(), JSON.stringify(hint));
    }
  }

  /**
   * The reset the Session Expired page needs - a stale or corrupt MSAL cache is the usual reason to land
   * there - but keeping the sign-in hints, so the next sign-in comes back with the
   * address pre-filled and the provider already known. An explicit logout drops the hint instead (see saveClaims).
   */
  static clearIdentityStorage(options?: { keepSignInHint?: boolean }) {
    const keepSignInHint = options?.keepSignInHint ?? true;
    const key = IdentityService.signInHintKey();
    const hint = keepSignInHint ? window.localStorage.getItem(key) : null;
    window.localStorage.clear();
    window.sessionStorage.clear();
    if (hint) {
      window.localStorage.setItem(key, hint);
    }
  }

  msalInstance: IPublicClientApplication;

  constructor(instance: IPublicClientApplication) {
    this.msalInstance = instance;
  }

  /**
   * The hints for the next sign-in redirect. A live account wins; the stored record is the fallback,
   * because the Session Expired reset drops both the MSAL account and the claims — without it a
   * returning user lands on an empty form and has to type their address again.
   */
  protected getSignInHint(account: AccountInfo | null | undefined) {
    const identityConfig = ConfigurationProvider.getConfiguration().Identity;
    const claims = this.getClaims();
    const stored = IdentityService.readSignInHint();
    return {
        loginHint:
            IdentityHelper.getLoginHint(account) ||
            IdentityHelper.getLoginHintFromClaims(claims) ||
            stored.loginHint,
        domainHint:
            IdentityHelper.getDomainHint(account, identityConfig.FederatedIdps, claims) ||
            stored.domainHint,
    };
  }

  /** Pre-fills the address on Entra's sign-in page so a returning user only has to press Next. */
  protected getLoginHint(account: AccountInfo | null | undefined): string | undefined {
    return this.getSignInHint(account).loginHint;
  }

  async login(url: string) {
    console.log("------------------ CALL TO LOGIN ---------------------");
    await this.msalInstance.handleRedirectPromise();
    const activeAccount = this.msalInstance.getActiveAccount(); // This will only return a non-null value if you have logic somewhere else that calls the setActiveAccount API
    const accounts = this.msalInstance.getAllAccounts();

    let request: RedirectRequest = {
      ...IdentityProvider.getBaseLoginRequest(),
      redirectStartPage: UrlUtility.join(ConfigurationProvider.getConfiguration().App.BaseUrl, url),
      redirectUri: IdentityService.redirectUri(),
      // login_hint claim (or username) lets Entra skip "Pick an account" and go straight to the user's IdP.
      loginHint: this.getLoginHint(activeAccount || accounts[0]),
    };

    if(window.parent !== window) return this.msalInstance.loginPopup(request).then(_ => {
      console.log("Redirect Back To URL 1");
      IdentityService.navigationClient.navigateSPA( UrlUtility.join("/",url))
    });
    return this.msalInstance.loginRedirect(request);
  }

  async logout() {
    console.log("------------------ CALL TO LOGOUT ---------------------");
    // Same prelude as login(): settles any pending redirect so a stale "interaction in progress" state cannot block us.
    await this.msalInstance.handleRedirectPromise();
    const activeAccount = this.msalInstance.getActiveAccount(); // This will only return a non-null value if you have logic somewhere else that calls the setActiveAccount API
    const accounts = this.msalInstance.getAllAccounts();

    const account = activeAccount || accounts[0];

    // Entra External ID: a wrong logout_hint (e.g. the display name) makes the sign-out page ask
    // "which account do you want to sign out of?". Use the id_token_hint when MSAL exposes the raw
    // ID token (v3+), else the login_hint claim; never the display name.
    const idToken = (account as AccountInfo & { idToken?: string })?.idToken;
    const loginHint = (account?.idTokenClaims as Partial<IdentityClaims> | undefined)?.login_hint;
    let request: EndSessionRequest = {
      postLogoutRedirectUri: IdentityService.redirectUri(),
      account,
      // id_token_hint alone identifies the session; only fall back to logout_hint when MSAL has no raw
      // ID token (msal-browser v2) and the login_hint claim is present. An email/username here is NOT
      // accepted by Entra as logout_hint and triggers the "which account?" page.
      idTokenHint: idToken,
      logoutHint: idToken ? undefined : loginHint,
    };
    // Only now that the request holds the id token it needs: drop everything this origin
    // stores, sign-in hints included. Signing out is deliberate, so the next person starts
    // from a clean form. MSAL clears its own cache as part of the sign-out redirect.
    IdentityService.clearIdentityStorage({ keepSignInHint: false });

    if(window.parent !== window) return this.msalInstance.logoutPopup(request);
    return this.msalInstance.logoutRedirect(request);

    // const requestIdToken = {
    //     ...IdentityProvider.getBaseLoginRequest(),
    //     scopes: ["openid"],
    //     account: activeAccount || accounts[0],
    //     forceRefresh: true,
    // };

    //    return this.msalInstance
    //    .acquireTokenSilent(requestIdToken)
    //    .then(async idAuthResult => {
    //         this.saveClaims(null);
    //         request.idTokenHint: idAuthResult.idToken;

    //         await this.msalInstance.handleRedirectPromise();
    //         return this.msalInstance.logoutRedirect(request);
    //     }).catch(err => {
    //         this.saveClaims(null);
    //         console.log(" :::: ", err)
    //         alert("problem" + err);
    //         IdentityService.navigationClient.navigateSPA("/");
    //     });
  }

  async loginBackToUrl(url: string) {
    console.log("------------------ CALL TO LOGIN ---------------------");

    await this.msalInstance.handleRedirectPromise();
    const activeAccount = this.msalInstance.getActiveAccount(); // This will only return a non-null value if you have logic somewhere else that calls the setActiveAccount API
    const accounts = this.msalInstance.getAllAccounts();

    let request: RedirectRequest = {
      ...IdentityProvider.getBaseLoginRequest(),
      redirectUri: IdentityService.redirectUri(),
      redirectStartPage: url,


      // login_hint claim (or username) lets Entra skip "Pick an account" and go straight to the user's IdP.
      loginHint: this.getLoginHint(activeAccount || accounts[0]),
    };

    if(window.parent !== window) return this.msalInstance.loginPopup(request).then(_ => {
      console.log("Redirect Back To URL 2");
      IdentityService.navigationClient.navigateSPA( UrlUtility.join("/",url))
    });
    return this.msalInstance.loginRedirect(request);
  }

  async getAuthenticationResult(): Promise<AuthenticationResult | void | null> {
    const activeAccount = this.msalInstance.getActiveAccount(); // This will only return a non-null value if you have logic somewhere else that calls the setActiveAccount API
    const accounts = this.msalInstance.getAllAccounts();

    if (IdentityService.inProgress !== InteractionStatus.None) {
      //Testing
      await this.msalInstance.handleRedirectPromise();
    }

    const request = {
      ...IdentityProvider.getBaseLoginRequest(),
      account: activeAccount || accounts[0],
    };

    // Silently acquires an access token which is then attached to a request for Microsoft Graph data
    return this.msalInstance
      .acquireTokenSilent(request)
      .then((authResult) => {
        console.log("Silent Sucess");
        this.saveClaims(authResult.idTokenClaims as IdentityClaims);
        return authResult;
      })
      .catch(async (e) => {
        console.log("AcquireTokenSilent Error ::", e);

        // MSAL surfaces "needs the user" as InteractionRequiredAuthError (errorCode interaction_required /
        // consent_required / login_required), never as e.name === "interaction_required". Those cases go
        // back through the login redirect — with the IdP session still alive that completes silently —
        // and only a genuine failure lands on the Session Expired page.
        if (IdentityHelper.isInteractionRequired(e)) {
         IdentityService.navigationClient.navigateSPA(`/identity/login?url=${window.location.pathname}`);

          return null;
          // return this.msalInstance
          //     .acquireTokenRedirect(redirectRequest)
          //     .catch((e) => {
          //         console.log("AcquireTokenSilent + Redirect Error ::", e);
          //         window.history.pushState({}, '',"/sessionexpired");
          //         return null;
          //     });;
        }
   
        IdentityService.navigationClient.navigateSPA(`/identity/sessionexpired?url=${window.location.pathname}`);

        return null;
      });
  }

  //Hearbeat purposes
  tryAquireAccessToken(): Promise<string | null> {
    const activeAccount = this.msalInstance.getActiveAccount(); // This will only return a non-null value if you have logic somewhere else that calls the setActiveAccount API
    const accounts = this.msalInstance.getAllAccounts();

    if (IdentityService.inProgress !== InteractionStatus.None) {
      return this.msalInstance
        .handleRedirectPromise()
        .then((authResult) => {
          if (authResult && (authResult as AuthenticationResult)) {
            this.saveClaims(authResult.idTokenClaims as IdentityClaims);
            return authResult.accessToken;
          }
          return null;
        })
        .catch((e) => {
          return null;
        });
    }

    const request = {
      ...IdentityProvider.getBaseLoginRequest(),
      account: activeAccount || accounts[0],
    };

    // const redirectRequest: RedirectRequest = {
    //   ...IdentityProvider.getBaseLoginRequest(),
    //   account: activeAccount || accounts[0]
    // }

    // Silently acquires an access token which is then attached to a request for Microsoft Graph data
    return this.msalInstance
      .acquireTokenSilent(request)
      .then((authResult) => {
        this.saveClaims(authResult.idTokenClaims as IdentityClaims);
        if (authResult && (authResult as AuthenticationResult)) {
          return authResult.accessToken;
        }
        return null;
      })
      .catch((e) => {
        return null;
      });
  }

  getAccessToken(): Promise<string | void> {
    return this.getAuthenticationResult().then((result) => {
      if (result && (result as AuthenticationResult)) {
        return result.accessToken;
      }
    });
  }


  saveClaims(claims: IdentityClaims | null) {
    let storageKey = ConfigurationProvider.getConfiguration().App.Name.replace("-", "_") + "_STORAGE_IDENTITY_CLAIMS";

    if (claims) {
      window.localStorage.setItem(storageKey, JSON.stringify(claims));

      // Remember the address and provider of this session; both have to outlive the Session Expired reset.
      IdentityService.saveSignInHint(claims);
    } else {
      window.localStorage.removeItem(storageKey);
      // Signing out is deliberate: forget the hints so the next visitor gets an empty sign-in page.
      window.localStorage.removeItem(IdentityService.signInHintKey());
    }
  }

  getClaims(): IdentityClaims {
    let storageKey = ConfigurationProvider.getConfiguration().App.Name.replace("-", "_") + "_STORAGE_IDENTITY_CLAIMS";

    let claims = window.localStorage.getItem(storageKey);
    return claims && JSON.parse(claims);
  }
}
